import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import { createGalleryImage, deleteGalleryImage, listGalleryImages } from '../../api/gallery';
import { API_BASE_URL } from '../../config';

export default function GallerySection({ token }) {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [caption, setCaption] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [busyGalleryId, setBusyGalleryId] = useState(null);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await listGalleryImages(token);
      setImages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      setSaveError('Select an image file');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const formData = new FormData();
      if (caption) formData.append('caption', caption);
      formData.append('image', imageFile);

      await createGalleryImage(token, formData);
      setCaption('');
      setImageFile(null);
      setSaveSuccess('Image uploaded');
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (image) => {
    if (!window.confirm('Delete this image?')) return;
    setBusyGalleryId(image.gallery_id);
    setError('');
    try {
      await deleteGalleryImage(token, image.gallery_id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyGalleryId(null);
    }
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Gallery</h2>

      <form
        onSubmit={handleUpload}
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6 space-y-3"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Caption (optional)
          </label>
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. Sunday Worship Service"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image file</label>
          <input
            required
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            className="w-full text-sm"
          />
        </div>

        {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        {saveSuccess && <p className="text-sm text-indigo-600">{saveSuccess}</p>}

        <button
          type="submit"
          disabled={isSaving}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? 'Uploading...' : 'Upload image'}
        </button>
      </form>

      <ErrorBanner message={error} onRetry={load} />

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : images.length === 0 ? (
        <p className="text-gray-400 text-sm">No images uploaded yet</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((img) => {
            const isBusy = busyGalleryId === img.gallery_id;
            return (
              <div
                key={img.gallery_id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col"
              >
                <img
                  src={`${API_BASE_URL}/gallery/${img.gallery_id}/image`}
                  alt={img.caption || 'Gallery'}
                  className="w-full h-32 object-cover"
                />
                <div className="p-2 flex-1 flex flex-col justify-between gap-2">
                  <p className="text-xs text-gray-600 truncate">{img.caption || '—'}</p>
                  <button
                    onClick={() => handleDelete(img)}
                    disabled={isBusy}
                    className="text-xs text-red-600 hover:underline disabled:opacity-50 self-start"
                  >
                    {isBusy ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
